<template>
  <div id="app">
    <router-view v-if="isAuthPage" />

    <template v-else>
      <Transition name="fade-backdrop">
        <div v-if="isSidebarOpen" class="sidebar-backdrop" @click="closeSidebar"></div>
      </Transition>
      <AdminSidebar
        :is-open="isSidebarOpen"
        :auth-user-email="authUserEmail"
        :auth-user-name="authUserName"
        :auth-user-role="authUserRole"
        :auth-user-image="authUserImage"
        :auth-user-initials="authUserInitials"
        :is-admin="isAdmin"
        @logout="logout"
        @navigate="closeSidebar"
      />
      <main class="admin-main">
        <div class="topbar">
          <div class="topbar-head">
            <div class="topbar-start">
              <p class="eyebrow">Optometry Academy</p>
              <h1>{{ pageTitle }}</h1>
            </div>
            <button
              class="btn btn-outline-primary topbar-refresh"
              type="button"
              :disabled="refreshLoading || !isAuthenticated"
              @click="refreshData"
            >
              <span v-if="refreshLoading" class="spinner-border spinner-border-sm"></span>
              <span v-else class="fas fa-sync-alt"></span>
              <span class="topbar-refresh-text">Обновить</span>
            </button>
            <button class="btn btn-outline-secondary topbar-toggle mobile-only" type="button" @click="toggleSidebar">
              <span class="fas" :class="isSidebarOpen ? 'fa-times' : 'fa-bars'"></span>
            </button>
          </div>
        </div>
        <router-view />
      </main>
    </template>
    <ProcessIndicator />
    <ConfirmModal />
    <NotificationStack />
  </div>
</template>

<script>
import AdminSidebar from './components/AdminSidebar.vue'
import ConfirmModal from './components/ConfirmModal.vue'
import NotificationStack from './components/NotificationStack.vue'
import ProcessIndicator from './components/ProcessIndicator.vue'
import { startAdminNotificationListeners } from './services/adminNotificationsService'

export default {
  components: {
    AdminSidebar,
    ConfirmModal,
    NotificationStack,
    ProcessIndicator
  },
  data() {
    return {
      isSidebarOpen: false,
      refreshLoading: false,
      unsubscribeAdminNotifications: null
    }
  },
  computed: {
    pageTitle() {
      return this.$route.meta.title || 'Admin'
    },
    isAuthPage() {
      return Boolean(this.$route.meta.public)
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated
    },
    isAdmin() {
      return this.$store.getters.isAdmin
    },
    authUserEmail() {
      const user = this.$store.getters.authUser
      return user ? user.email : ''
    },
    authProfile() {
      return this.$store.getters.authProfile || {}
    },
    authUserRole() {
      return this.authProfile.role || ''
    },
    authUserName() {
      return this.authProfile.name || ''
    },
    authUserImage() {
      return this.authProfile.image || ''
    },
    authUserInitials() {
      return (this.authUserName || this.authUserEmail || 'A').slice(0, 1).toUpperCase()
    },
    db() {
      return this.$store.getters.db
    }
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen
    },
    closeSidebar() {
      this.isSidebarOpen = false
    },
    async refreshData() {
      if (this.refreshLoading || !this.isAuthenticated) return

      const refreshTasks = []
      window.dispatchEvent(new CustomEvent('admin:refresh-data', {
        detail: { tasks: refreshTasks }
      }))

      this.refreshLoading = true
      try {
        await Promise.all([
          this.$store.dispatch('fetchDb'),
          ...refreshTasks
        ])
        await this.$store.dispatch('notify', {
          type: 'success',
          title: 'Данные обновлены'
        })
      } catch (error) {
        if (!error) return
      } finally {
        this.refreshLoading = false
      }
    },
    async logout() {
      const confirmed = await this.$confirm({
        title: 'Выйти из админки?',
        message: 'Вы завершите текущую сессию и вернетесь на страницу входа.',
        confirmText: 'Выйти',
        cancelText: 'Остаться',
        variant: 'danger'
      })

      if (!confirmed) return

      this.$store.dispatch('logout')
        .then(() => this.$router.replace('/login'))
    },
    startNotifications() {
      if (this.unsubscribeAdminNotifications) return
      this.unsubscribeAdminNotifications = startAdminNotificationListeners({
        store: this.$store
      })
    },
    stopNotifications() {
      if (!this.unsubscribeAdminNotifications) return
      this.unsubscribeAdminNotifications()
      this.unsubscribeAdminNotifications = null
    }
  },
  beforeUnmount() {
    this.stopNotifications()
  },
  watch: {
    $route() {
      this.closeSidebar()
    },
    isAuthenticated: {
      immediate: true,
      handler(isAuthenticated) {
        if (isAuthenticated && !this.db) {
          this.$store.dispatch('fetchDb')
        }
        if (isAuthenticated) {
          this.startNotifications()
        } else {
          this.stopNotifications()
        }
        if (!isAuthenticated && !this.isAuthPage) {
          this.$router.replace({
            path: '/login',
            query: this.$route.fullPath && this.$route.fullPath !== '/' ? { redirect: this.$route.fullPath } : {}
          })
        }
      }
    }
  }
}
</script>

<style lang="less">
@import "./assets/less/index.less";
</style>
